/**
 * Create Actions function for Reflux replacement.
 * Creates multiple actions from an array of names or object of definitions.
 *
 * @module reflux/createActions
 */

import { createAction } from './createAction.js';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Processes a single definition item into an action.
 * Handles string names, object definitions, and empty values.
 *
 * @param {string|Object|null|undefined} item - Definition item to process
 * @param {string} [keyName] - Key name when processing object definitions
 * @returns {Function} Created action
 * @throws {Error} If definition is invalid
 */
const processDefinitionItem = (item, keyName = null) => {
  // String: use as action name
  if (typeof item === 'string') {
    return createAction(item);
  }

  // Object: merge with keyName if provided
  if (typeof item === 'object' && item !== null) {
    const name = keyName || item.actionName;
    if (!name) {
      throw new Error('createActions: definition object must have actionName');
    }
    return createAction({ ...item, actionName: name });
  }

  // Empty (null/undefined): use keyName as action name
  if (item === undefined || item === null) {
    if (!keyName) {
      throw new Error('createActions: empty definition requires a key name');
    }
    return createAction(keyName);
  }

  throw new TypeError('createActions: definition must be string, object, or empty');
};

/**
 * Processes array-style definitions: ['action1', 'action2', ...]
 *
 * @param {Array} definitions - Array of action definitions
 * @returns {Object} Actions object keyed by action name
 */
const processArrayDefinitions = (definitions) => {
  const actions = {};

  for (const item of definitions) {
    if (typeof item === 'string') {
      actions[item] = processDefinitionItem(item);
    } else if (typeof item === 'object' && item !== null) {
      const name = item.actionName;
      if (!name) {
        throw new Error('createActions: array item object must have actionName property');
      }
      actions[name] = processDefinitionItem(item, name);
    } else {
      throw new TypeError('createActions: array items must be strings or objects');
    }
  }

  return actions;
};

/**
 * Processes object-style definitions: { action1: {...}, action2: {...} }
 *
 * @param {Object} definitions - Object of action definitions
 * @returns {Object} Actions object keyed by action name
 */
const processObjectDefinitions = (definitions) => {
  const actions = {};

  for (const [actionName, definition] of Object.entries(definitions)) {
    actions[actionName] = processDefinitionItem(definition, actionName);
  }

  return actions;
};

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Creates multiple actions from array or object definitions.
 *
 * @param {string[]|Object} definitions - Array of action names or object of action definitions
 * @returns {Object} Object with action names as keys and action functions as values
 *
 * @example
 * // From array of strings
 * const actions = createActions(['login', 'logout', 'signup']);
 * actions.login();
 * actions.logout();
 *
 * @example
 * // From object with mixed definitions
 * const UserActions = createActions({
 *   login: { asyncResult: true, children: ['complete', 'fail'] },
 *   logout: { asyncResult: true, children: ['complete', 'fail'] },
 *   trackEvent: {},
 *   initialize: {}
 * });
 *
 * UserActions.login.listen(function(credentials) {
 *   UserService.login(credentials)
 *     .then(this.complete)
 *     .catch(this.fail);
 * });
 */
export const createActions = (definitions) => {
  if (!definitions) {
    throw new TypeError('createActions: definitions is required');
  }

  if (Array.isArray(definitions)) {
    return processArrayDefinitions(definitions);
  }

  if (typeof definitions === 'object') {
    return processObjectDefinitions(definitions);
  }

  throw new TypeError('createActions: definitions must be array or object');
};
